// Getting to a property: the address as one line, and links that open the
// phone's own maps app with directions. When the Worker geocoded the
// address the links carry the point (exact, even for a new build the maps
// app cannot find by name); otherwise they carry the address text.

export interface Addressed {
  readonly addressLine1: string;
  readonly addressLine2: string;
  readonly city: string;
  readonly region: string;
  readonly postalCode: string;
}

export interface Located extends Addressed {
  readonly latitude: number | null;
  readonly longitude: number | null;
}

/** The address on one line, or '' when nothing is filled in. */
export function addressText(address: Addressed): string {
  const cityLine = [address.city, address.region].filter((part) => part !== '').join(', ');
  return [address.addressLine1, address.addressLine2, cityLine, address.postalCode]
    .filter((part) => part !== '')
    .join(', ');
}

export function hasLocation(place: Located): boolean {
  return place.latitude !== null && place.longitude !== null;
}

/** What the directions links point at: the point when known, else the words. */
function destination(place: Located): string {
  return place.latitude !== null && place.longitude !== null
    ? `${String(place.latitude)},${String(place.longitude)}`
    : addressText(place);
}

export interface DirectionsLink {
  readonly label: string;
  readonly href: string;
}

/** Links for the two maps apps a phone is likely to have; empty when nothing to go to. */
export function directionsLinks(place: Located): DirectionsLink[] {
  const to = destination(place);
  if (to === '') {
    return [];
  }
  const encoded = encodeURIComponent(to);
  return [
    { label: 'Google Maps', href: `https://www.google.com/maps/dir/?api=1&destination=${encoded}` },
    { label: 'Apple Maps', href: `https://maps.apple.com/?daddr=${encoded}` },
  ];
}
